import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { QueueStatusService } from '../../../services/Integration/queue-status.service';
import { IntegrationErrorNotificationService } from '../../../services/Integration/integration-error-notification.service';
import {
  SyncProvider,
  TypedSyncResult,
  isProjectsSyncResult,
  isP6SyncResult,
  isObservationsSyncResult,
} from '../../../models/integration.model';
import { ProjectsSyncResultsModalComponent } from '../projects-sync-results-modal/projects-sync-results-modal.component';
import { P6SyncResultsModalComponent } from '../p6-sync-results-modal/p6-sync-results-modal.component';
import { ObservationsSyncResultsModalComponent } from '../observations-sync-results-modal/observations-sync-results-modal.component';

/**
 * Last Sync Results Button Component
 * Provider-agnostic button for viewing last sync results
 * Uses union types and type guards for type-safe handling
 */
@Component({
  selector: 'app-last-sync-results-button',
  templateUrl: './last-sync-results-button.component.html',
  styleUrls: ['./last-sync-results-button.component.scss'],
})
export class LastSyncResultsButtonComponent implements OnInit, OnDestroy {
  // Input properties
  @Input() provider: SyncProvider = 'projects';
  @Input() projectId?: string;
  @Input() disabled: boolean = false;
  @Input() showButton: boolean = true;

  // Output events
  @Output() resultsClicked = new EventEmitter<TypedSyncResult>();

  // Public properties
  isLoading: boolean = false;
  buttonColor: 'primary' | 'accent' = 'accent';

  // Provider-specific labels
  providerLabels = {
    projects: {
      lastResultsButton: 'integration.show_last_results',
      noResultsFound: 'integration.no_results_found',
      parsingError: 'integration.parsing_results_error',
    },
    p6: {
      lastResultsButton: 'integration.show_last_results',
      noResultsFound: 'integration.no_results_found',
      parsingError: 'integration.parsing_results_error',
    },
    observations: {
      lastResultsButton: 'integration.show_last_results',
      noResultsFound: 'integration.no_results_found',
      parsingError: 'integration.parsing_results_error',
    },
  };

  // Private properties
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    private _queueStatusService: QueueStatusService,
    private _errorService: IntegrationErrorNotificationService,
    private _dialog: MatDialog,
    private _transloco: TranslocoService,
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    this._checkForUnviewedResults();

    // React to queue completion events so the button color updates immediately
    this._queueStatusService.queueCompleted$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ provider, projectId }) => {
        // Only update for matching provider/project (projects is global)
        const isSameProvider = provider === this.provider;
        const isSameProject =
          provider === 'projects' || this.projectId === (projectId || undefined);
        if (isSameProvider && isSameProject) {
          this._checkForUnviewedResults();
        }
      });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get provider-specific label
   */
  getLabel(key: string): string {
    const labels = this.providerLabels[this.provider];
    return this._transloco.translate(labels[key as keyof typeof labels] || key);
  }

  /**
   * Handle button click to show last sync results
   */
  onShowLastSyncResults(): void {
    if (this.isLoading || this.disabled) {
      return;
    }

    this.isLoading = true;

    try {
      const result = this._queueStatusService.getLastSyncResult(this.provider, this.projectId);

      console.log('last sync results button result', result);

      if (!result) {
        this._errorService.showInfo(this.getLabel('noResultsFound'));
        this.isLoading = false;
        return;
      }

      // Emit the result for parent components
      this.resultsClicked.emit(result);

      console.log('last sync results button result', result);
      // Show the appropriate modal based on provider type
      this._showResultsModal(result);

      // Mark as viewed
      this._queueStatusService.updateQueueTracker(this.provider, { viewed: true }, this.projectId);
      this._checkForUnviewedResults();
    } catch (error) {
      console.error('last sync results button error', error);
      this._errorService.showError(this.getLabel('parsingError'));
    } finally {
      this.isLoading = false;
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Check for unviewed results and update button color
   */
  private _checkForUnviewedResults(): void {
    const queueTracker = this._queueStatusService.getQueueTracker(this.provider, this.projectId);
    const hasUnviewedResults = !queueTracker?.viewed && queueTracker?.completed;
    this.buttonColor = hasUnviewedResults ? 'primary' : 'accent';
  }

  /**
   * Show the appropriate modal based on result type using type guards
   */
  private _showResultsModal(result: TypedSyncResult): void {
    if (isProjectsSyncResult(result)) {
      this._showProjectsModal(result);
    } else if (isP6SyncResult(result)) {
      this._showP6Modal(result);
    } else if (isObservationsSyncResult(result)) {
      this._showObservationsModal(result);
    } else {
      console.error('Unknown sync result type:', result);
      this._errorService.showError('Unknown sync result type');
    }
  }

  /**
   * Show projects sync results modal
   */
  private _showProjectsModal(result: any): void {
    const dialogRef = this._dialog.open(ProjectsSyncResultsModalComponent, {
      width: '800px',
      maxWidth: '90vw',
      data: { syncResult: result },
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((shouldRefresh) => {
      if (shouldRefresh) {
        window.location.reload();
      }
    });
  }

  /**
   * Show P6 sync results modal
   */
  private _showP6Modal(result: any): void {
    const dialogRef = this._dialog.open(P6SyncResultsModalComponent, {
      width: '800px',
      maxWidth: '90vw',
      data: { syncResult: result },
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((shouldRefresh) => {
      if (shouldRefresh) {
        window.location.reload();
      }
    });
  }

  /**
   * Show observations sync results modal
   */
  private _showObservationsModal(result: any): void {
    const dialogRef = this._dialog.open(ObservationsSyncResultsModalComponent, {
      width: '800px',
      maxWidth: '90vw',
      data: { syncResult: result },
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((shouldRefresh) => {
      if (shouldRefresh) {
        window.location.reload();
      }
    });
  }
}
